import React from 'react';
import { Box, Typography, Skeleton } from '@mui/material';

const TONE_COLORS = {
  success: '#2E7D32',
  warning: '#C77700',
  error: '#C62828',
  default: '#2c2a1e',
};

const TONE_ICON_BG = {
  success: 'rgba(46,125,50,0.12)',
  warning: 'rgba(199,119,0,0.12)',
  error: 'rgba(198,40,40,0.12)',
  default: 'rgba(44,42,30,0.08)',
};

/**
 * Compact single-figure stat card for a page header — sized to match CompactMetricCard's
 * 54px icon slot + padding so financial totals (which have no categorical breakdown to
 * donut-chart or drill into) can sit in the same header row at the same height.
 *
 * Props:
 *   title     — short label (e.g. "Total Billed")
 *   value     — pre-formatted display string (e.g. "$12,430.00")
 *   tone      — 'success' | 'warning' | 'error' | 'default' (default), colors the value text + icon
 *   icon      — optional MUI icon component, rendered in a 54x54 slot matching the donut card
 *   isLoading — while true, renders a skeleton placeholder
 */
export const CompactStatCard = ({ title, value, tone = 'default', icon: Icon, isLoading = false }) => {
  const toneColor = TONE_COLORS[tone] || TONE_COLORS.default;

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex', alignItems: 'center', gap: 1,
          bgcolor: 'rgba(255,255,255,0.72)', border: '1px solid rgba(0,0,0,0.06)',
          borderRadius: 2, px: 1.25, py: 0.5,
        }}
      >
        {Icon && <Skeleton variant="circular" width={54} height={54} sx={{ flexShrink: 0 }} />}
        <Box sx={{ minWidth: 0, pr: 0.5 }}>
          <Skeleton variant="text" width={70} sx={{ fontSize: 11 }} />
          <Skeleton variant="text" width={80} sx={{ fontSize: 16 }} />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex', alignItems: 'center', gap: 1,
        bgcolor: 'rgba(255,255,255,0.72)', border: '1px solid rgba(0,0,0,0.06)',
        borderRadius: 2, px: 1.25, py: 0.5,
      }}
    >
      {Icon && (
        <Box
          sx={{
            width: 54, height: 54, flexShrink: 0, borderRadius: '50%',
            bgcolor: TONE_ICON_BG[tone] || TONE_ICON_BG.default,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <Icon sx={{ fontSize: 26, color: toneColor }} />
        </Box>
      )}
      <Box sx={{ minWidth: 0, pr: 0.5 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#2c2a1e', lineHeight: 1.2, whiteSpace: 'nowrap' }}>
          {title}
        </Typography>
        <Typography sx={{ fontSize: 16, fontWeight: 700, color: toneColor, lineHeight: 1.3, whiteSpace: 'nowrap' }}>
          {value}
        </Typography>
      </Box>
    </Box>
  );
};

export default CompactStatCard;
